import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Grab, Tier, Rechnung, Rechnungsvorschau } from '../types';
import grabService from '../services/grabService';
import tierService from '../services/tierService';
import rechnungService from '../services/rechnungService';
import { useFriedhof } from '../contexts/FriedhofContext';
import { STATUS_FILL, STATUS_LABEL } from '../components/lageplan/leafletUtils';
import { Card, LoadingScreen, Icon, GrabStein } from '../components/ui';
import { fmtDatum } from '../config/rechnungStatus';
import { formatBetrag } from '../utils/preis';

/**
 * Dashboard: der Friedhof auf einen Blick und was heute zu tun ist.
 *
 * Oben jedes Grab als kleiner Grabstein in seiner Statusfarbe (dieselben Farben wie im Lageplan),
 * nach Abteilung gruppiert – so sieht man sofort, wo noch Platz ist. Darunter die offenen Aufgaben
 * aus der Abrechnung und die zuletzt beigesetzten Tiere. Bewusst keine Kennzahl-Kacheln: jede Zahl
 * steht in einem Satz oder führt zu einer Aufgabe.
 */

const STATUS_REIHENFOLGE = ['FREI', 'RESERVIERT', 'IN_NUTZUNG', 'BELEGT'] as const;
// Ab so vielen Gräbern werden die Steine zu klein – dann je Abteilung ein gestapelter Balken.
const MAX_STEINE = 400;
// Im Satz klein – „In Nutzung“ bleibt ein Substantiv.
const STATUS_IM_SATZ: Record<string, string> = { FREI: 'frei', RESERVIERT: 'reserviert', IN_NUTZUNG: 'in Nutzung', BELEGT: 'belegt' };

const abteilungSort = (a: string, b: string) => a.localeCompare(b, 'de', { numeric: true });
const grabSort = (a: Grab, b: Grab) => a.grabnummer.localeCompare(b.grabnummer, 'de', { numeric: true });

const Dashboard: React.FC = () => {
  const { friedhofId, friedhofName } = useFriedhof();
  const [graeber, setGraeber] = useState<Grab[]>([]);
  const [tiere, setTiere] = useState<Tier[]>([]);
  const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
  const [vorschau, setVorschau] = useState<Rechnungsvorschau | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!friedhofId) return;
    try {
      const [g, t, r, v] = await Promise.all([
        grabService.getByFriedhof(friedhofId),
        tierService.getAll(),
        rechnungService.getAll().catch(() => [] as Rechnung[]),
        rechnungService.vorschau().catch(() => null),
      ]);
      const grabIds = new Set(g.map(x => x.id));
      setGraeber(g);
      setTiere(t.filter(x => grabIds.has(x.grabId)));
      setRechnungen(r);
      setVorschau(v);
    } catch { toast.error('Dashboard konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [friedhofId]);

  useEffect(() => { load(); }, [load]);

  const abteilungen = useMemo(() => {
    const m = new Map<string, Grab[]>();
    graeber.forEach(g => m.set(g.abteilung, [...(m.get(g.abteilung) ?? []), g]));
    return Array.from(m.entries()).sort(([a], [b]) => abteilungSort(a, b))
      .map(([name, liste]) => ({ name, graeber: liste.sort(grabSort) }));
  }, [graeber]);

  const anzahl = useMemo(() => {
    const z: Record<string, number> = { FREI: 0, RESERVIERT: 0, IN_NUTZUNG: 0, BELEGT: 0 };
    graeber.forEach(g => { z[g.status] = (z[g.status] ?? 0) + 1; });
    return z;
  }, [graeber]);

  const grabVon = useMemo(() => new Map(graeber.map(g => [g.id, g])), [graeber]);
  const zuletzt = useMemo(
    () => tiere.filter(t => t.beisetzungsdatum)
      .sort((a, b) => (b.beisetzungsdatum ?? '').localeCompare(a.beisetzungsdatum ?? ''))
      .slice(0, 5),
    [tiere],
  );

  if (loading) return <LoadingScreen />;

  const heute = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const belegt = graeber.length - anzahl.FREI;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Der Friedhof auf einen Blick */}
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">{friedhofName || 'Friedhof'}</h1>
            <p className="mt-1 text-sm text-gray-500">{heute}</p>
          </div>
          {friedhofId && (
            <Link
              to={`/lageplan/${friedhofId}`}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              <Icon.Map className="h-4 w-4" />Lageplan öffnen
            </Link>
          )}
        </div>

        {graeber.length === 0 ? (
          <p className="mt-6 text-sm text-gray-500">
            Für diesen Friedhof sind noch keine Gräber erfasst. <Link to="/graeber" className="font-medium text-brand-700 hover:underline">Grab anlegen</Link>
          </p>
        ) : (
          <>
            <p className="mt-6 text-base text-gray-700">
              {anzahl.FREI === 0
                ? <>Alle {graeber.length} Gräber sind vergeben.</>
                : <><span className="font-semibold text-gray-900">{anzahl.FREI} {anzahl.FREI === 1 ? 'Grab ist' : 'Gräber sind'} frei</span>, {belegt} von {graeber.length} vergeben.</>}
            </p>

            {graeber.length > MAX_STEINE ? (
              <Balkenfeld abteilungen={abteilungen} />
            ) : (
              <>
                <Steinfeld abteilungen={abteilungen} />
                <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                  {STATUS_REIHENFOLGE.map(s => (
                    <li key={s} className="inline-flex items-center gap-2">
                      <GrabStein farbe={STATUS_FILL[s]} klein />
                      <span><span className="font-semibold tabular-nums text-gray-900">{anzahl[s]}</span> {STATUS_IM_SATZ[s]}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <ZuErledigen rechnungen={rechnungen} vorschau={vorschau} />

        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900">Zuletzt beigesetzt</h2>
          {zuletzt.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">
              Noch keine Beisetzung erfasst. <Link to="/tiere" className="font-medium text-brand-700 hover:underline">Zu den Tieren</Link>
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100">
              {zuletzt.map(t => {
                const g = grabVon.get(t.grabId);
                return (
                  <li key={t.id}>
                    <Link to={`/tiere/${t.id}`} className="-mx-2 flex items-baseline gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-gray-50">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900">
                          {t.name || 'Ohne Namen'}{t.art && <span className="font-normal text-gray-500">, {t.art}</span>}
                        </span>
                        {g && <span className="block text-sm text-gray-500">Abteilung {g.abteilung}, Grab {g.grabnummer}</span>}
                      </span>
                      <span className="shrink-0 text-sm tabular-nums text-gray-500">{fmtDatum(t.beisetzungsdatum)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

/**
 * Jedes Grab als Stein, gruppiert nach Abteilung. Die Gruppen fließen nebeneinander – kleine
 * Abteilungen teilen sich eine Zeile, große füllen eine eigene. Tippen öffnet das Grab.
 */
const Steinfeld: React.FC<{ abteilungen: { name: string; graeber: Grab[] }[] }> = ({ abteilungen }) => (
  <div className="mt-5 flex flex-wrap gap-x-8 gap-y-4">
    {abteilungen.map(a => (
      <div key={a.name} className="min-w-0">
        <span className="mb-1.5 block text-xs font-medium text-gray-500">Abteilung {a.name}</span>
        <div className="flex flex-wrap gap-1.5">
          {a.graeber.map(g => (
            <Link
              key={g.id}
              to={`/graeber/${g.id}`}
              title={`Abteilung ${g.abteilung}, Grab ${g.grabnummer}: ${STATUS_LABEL[g.status] ?? g.status}`}
              className="rounded-t-full p-0.5 transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
            >
              <GrabStein farbe={STATUS_FILL[g.status] ?? '#9ca3af'} />
              <span className="sr-only">Grab {g.grabnummer}, {STATUS_LABEL[g.status]}</span>
            </Link>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Große Friedhöfe: je Abteilung ein Balken, anteilig nach Status, rechts daneben die Zahlen je
 * Status als eigene Spalten (untereinander vergleichbar, auch wenn ein Segment zu schmal für eine
 * Zahl ist). Spalten nur für Status, die es auf dem Friedhof gibt; eine 0 bleibt leer.
 */
const Balkenfeld: React.FC<{ abteilungen: { name: string; graeber: Grab[] }[] }> = ({ abteilungen }) => {
  const zeilen = abteilungen.map(a => ({
    name: a.name,
    gesamt: a.graeber.length,
    je: Object.fromEntries(STATUS_REIHENFOLGE.map(s => [s, a.graeber.filter(g => g.status === s).length])) as Record<string, number>,
  }));
  const summe = (s: string) => zeilen.reduce((acc, z) => acc + z.je[s], 0);
  const spalten = STATUS_REIHENFOLGE.filter(s => summe(s) > 0);
  const gesamt = zeilen.reduce((acc, z) => acc + z.gesamt, 0);
  const raster = { gridTemplateColumns: `2rem minmax(3rem, 1fr) repeat(${spalten.length + 1}, minmax(1.5rem, auto))` };
  const zahl = 'text-right text-sm tabular-nums';

  return (
    <div className="mt-5 grid items-center gap-x-2 gap-y-2 sm:gap-x-4" style={raster}>
      <span />
      <span />
      {spalten.map(s => (
        <span key={s} className="flex items-center justify-end gap-1.5 text-xs font-medium text-gray-500">
          <GrabStein farbe={STATUS_FILL[s]} klein />
          <span className="hidden sm:inline">{STATUS_IM_SATZ[s]}</span>
        </span>
      ))}
      <span className="text-right text-xs font-medium text-gray-500"><span className="sm:hidden">ges.</span><span className="hidden sm:inline">gesamt</span></span>

      {zeilen.map(z => (
        <React.Fragment key={z.name}>
          <span className="text-right text-sm font-medium tabular-nums text-gray-500">{z.name}</span>
          <div className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-gray-100"
            title={spalten.map(s => `${z.je[s]} ${STATUS_IM_SATZ[s]}`).join(', ')}>
            {STATUS_REIHENFOLGE.map(s => z.je[s] > 0 && (
              <span key={s} style={{ flexGrow: z.je[s], flexBasis: 0, backgroundColor: STATUS_FILL[s] }} />
            ))}
          </div>
          {spalten.map(s => (
            <span key={s} className={`${zahl} text-gray-900`}>{z.je[s] || ''}</span>
          ))}
          <span className={`${zahl} text-gray-500`}>{z.gesamt}</span>
        </React.Fragment>
      ))}

      <span className="col-span-full mt-1 h-px bg-gray-200" />
      <span className="text-right text-xs font-medium text-gray-500"><span className="sm:hidden">Σ</span><span className="hidden sm:inline">Summe</span></span>
      <span />
      {spalten.map(s => (
        <span key={s} className={`${zahl} font-semibold text-gray-900`}>{summe(s)}</span>
      ))}
      <span className={`${zahl} font-semibold text-gray-900`}>{gesamt}</span>
    </div>
  );
};

/**
 * Was aus der Abrechnung ansteht – nur Einträge, bei denen es etwas zu tun gibt, jeder führt
 * direkt in die passend gefilterte Rechnungsliste.
 */
const ZuErledigen: React.FC<{ rechnungen: Rechnung[]; vorschau: Rechnungsvorschau | null }> = ({ rechnungen, vorschau }) => {
  const entwuerfe = rechnungen.filter(r => r.status === 'ENTWURF');
  // Storno-Rechnungen (negativ) sind keine Forderung, auch wenn sie als gestellt geführt werden.
  const ueberfaellig = rechnungen.filter(r => r.ueberfaellig && r.summeBrutto > 0);
  const vorgemerkt = vorschau?.rechnungen ?? [];
  const summe = (l: { summeBrutto: number }[]) => l.reduce((s, r) => s + r.summeBrutto, 0);

  const aufgaben = [
    entwuerfe.length > 0 && {
      key: 'entwuerfe', to: '/rechnungen?status=ENTWURF', ton: 'amber',
      text: `${entwuerfe.length} ${entwuerfe.length === 1 ? 'Rechnungsentwurf' : 'Rechnungsentwürfe'} prüfen und stellen`,
      betrag: summe(entwuerfe),
    },
    ueberfaellig.length > 0 && {
      key: 'ueberfaellig', to: '/rechnungen?status=UEBERFAELLIG', ton: 'red',
      text: `${ueberfaellig.length} ${ueberfaellig.length === 1 ? 'Rechnung ist' : 'Rechnungen sind'} überfällig`,
      betrag: summe(ueberfaellig),
    },
    vorgemerkt.length > 0 && vorschau && {
      key: 'vorgemerkt', to: '/rechnungen?vorschau=1', ton: 'gray',
      text: `${vorgemerkt.length} ${vorgemerkt.length === 1 ? 'Rechnung entsteht' : 'Rechnungen entstehen'} bis ${fmtDatum(vorschau.bis).slice(0, 6)}`,
      betrag: summe(vorgemerkt),
    },
  ].filter(Boolean) as { key: string; to: string; ton: 'amber' | 'red' | 'gray'; text: string; betrag: number }[];

  const punkt = { amber: 'bg-amber-400', red: 'bg-red-500', gray: 'border-2 border-gray-300 bg-white' };

  return (
    <Card className="p-6">
      <h2 className="text-base font-semibold text-gray-900">Zu erledigen</h2>
      {aufgaben.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">In der Abrechnung ist gerade nichts offen.</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {aufgaben.map(a => (
            <li key={a.key}>
              <Link to={a.to} className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-gray-50">
                <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${punkt[a.ton]}`} />
                <span className="min-w-0 flex-1 font-medium text-gray-900">{a.text}</span>
                <span className={`shrink-0 text-sm tabular-nums ${a.ton === 'gray' ? 'text-gray-400' : 'text-gray-600'}`}>{formatBetrag(a.betrag)}</span>
                <Icon.ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default Dashboard;
